/**
 * Platform documentation registry — the single source of truth for the config app's F1 / `?` help.
 *
 * Every help topic is authored as markdown under ./ (this help/ folder), MANAGED in the AcademyContent table
 * (seeded via PlatformDocs-Seed.js), and served cross-tenant by the API's `GET /1.0/PlatformDocument/:Hash`
 * (canonical customer-1 copy + per-customer override). The app fetches by Hash and injects into the shared
 * inline-documentation panel (the Sample Plan displayContent bridge); pressing F1 on a route resolves the doc
 * via ROUTE_DOC, and a per-area nav bar lets the reader move between sibling topics in the same feature.
 *
 * Pure data + helpers (no pict / REST / fs), so this is safe in the browser bundle AND in the Node seed
 * generator. Markdown content lives in the .md files, never here — it stays out of the browser bundle.
 *
 * Hashes are `Doc-<Area>-<Topic>` — one `Doc-` namespace, so the API allowlist stays a single entry. To add a
 * topic: add an entry below + a matching .md file under help/<area>/, then reseed (see PlatformDocs-Seed.js).
 * Each doc: { Topic, Title, Nav, File, Routes } — Routes are the F1 targets (empty = reachable only via the nav).
 */

'use strict';

const AREAS =
[
	{
		Area: 'Import',
		Docs:
		[
			{ Topic: 'Overview',        Title: 'Importing Contracts, Projects & Items', Nav: 'Overview',            File: 'import/overview.md',         Routes: [] },
			{ Topic: 'ContractPackage', Title: 'Import Contract Package',                Nav: 'Contract Package',    File: 'import/contract-package.md', Routes: [ '/ContractImport' ] },
			{ Topic: 'LineItems',       Title: 'Import Line Items',                      Nav: 'Line Items',          File: 'import/line-items.md',       Routes: [ '/ItemImport' ] },
			{ Topic: 'Example',         Title: 'Example: importing a contract',          Nav: 'Example',             File: 'import/example.md',          Routes: [] },
			{ Topic: 'Cleanup',         Title: 'Fixing a bad import',                    Nav: 'Fixing a bad import', File: 'import/cleanup.md',          Routes: [] },
		],
	},
	{
		Area: 'Access',
		Docs:
		[
			{ Topic: 'Overview',  Title: 'Access & Permissions',              Nav: 'Overview',       File: 'access/overview.md',  Routes: [ '/Access' ] },
			{ Topic: 'Matrix',    Title: 'The Permission Matrix',             Nav: 'Matrix',         File: 'access/matrix.md',    Routes: [] },
			{ Topic: 'Roles',     Title: 'Roles, Groups & Composition',       Nav: 'Roles & Groups', File: 'access/roles.md',     Routes: [] },
			{ Topic: 'Effective', Title: 'Assignment & Effective Permissions', Nav: 'Effective',      File: 'access/effective.md', Routes: [] },
		],
	},
	{
		Area: 'ApprovalWorkflow',
		Docs:
		[
			{ Topic: 'Overview', Title: 'Approval Workflows',           Nav: 'Overview', File: 'approval-workflow/overview.md', Routes: [ '/ApprovalWorkflows', '/ApprovalWorkflow' ] },
			{ Topic: 'Editor',   Title: 'Editing an approval workflow', Nav: 'Editor',   File: 'approval-workflow/editor.md',   Routes: [] },
			{ Topic: 'Bindings', Title: 'Binding types to workflows',   Nav: 'Bindings', File: 'approval-workflow/bindings.md', Routes: [] },
		],
	},
	{
		Area: 'ObservationType',
		Docs:
		[
			{ Topic: 'Overview',     Title: 'Observation Types',             Nav: 'Overview',     File: 'observation-type/overview.md',     Routes: [ '/ObservationType' ] },
			{ Topic: 'Requirements', Title: 'Requirements & Artifact Types', Nav: 'Requirements', File: 'observation-type/requirements.md', Routes: [] },
			{ Topic: 'Building',     Title: 'Building & cloning a type',     Nav: 'Building',     File: 'observation-type/building.md',     Routes: [] },
		],
	},
	{
		Area: 'Config',
		Docs:
		[
			{ Topic: 'Overview', Title: 'Configuration Explorer',   Nav: 'Overview', File: 'config/overview.md', Routes: [ '/ConfigViewer' ] },
			{ Topic: 'Editing',  Title: 'Editing at a scope',       Nav: 'Editing',  File: 'config/editing.md',  Routes: [] },
			{ Topic: 'Cloning',  Title: 'Cloning between contexts', Nav: 'Cloning',  File: 'config/cloning.md',  Routes: [ '/ConfigClone' ] },
		],
	},
	{
		Area: 'DataImport',
		Docs:
		[
			{ Topic: 'Overview', Title: 'Import Data',    Nav: 'Overview', File: 'dataimport/overview.md', Routes: [ '/DataImport' ] },
			{ Topic: 'Mapping',  Title: 'Mapping & keys', Nav: 'Mapping',  File: 'dataimport/mapping.md',  Routes: [] },
		],
	},
	{
		Area: 'Datalake',
		Docs:
		[
			{ Topic: 'Overview',   Title: 'The Data Lake Catalog',      Nav: 'Overview',   File: 'datalake/overview.md',   Routes: [ '/Datalake' ] },
			{ Topic: 'Projection', Title: 'Creating a projection',      Nav: 'Projection', File: 'datalake/projection.md', Routes: [ '/Datalake/New' ] },
			{ Topic: 'Datasets',   Title: 'Browsing & managing datasets', Nav: 'Datasets',   File: 'datalake/datasets.md',   Routes: [ '/Datalake/Dataset', '/Datalake/Table' ] },
		],
	},
	{
		Area: 'Entitlements',
		Docs:
		[
			{ Topic: 'Overview', Title: 'Entitlements',            Nav: 'Overview',       File: 'entitlements/overview.md', Routes: [ '/Entitlements' ] },
			{ Topic: 'Tasks',    Title: 'How tasks map to access', Nav: 'Tasks & access', File: 'entitlements/tasks.md',    Routes: [] },
		],
	},
];

/** Hash for an (area, topic): the single `Doc-` namespace the API allowlist gates on. */
function hashFor(pArea, pTopic)
{
	return `Doc-${pArea}-${pTopic}`;
}

// Flatten to the lookups the app + seed use: DOCS (one per topic, with computed Hash) + ROUTE_DOC (route→Hash).
const DOCS = [];
const ROUTE_DOC = [];
AREAS.forEach((pArea) =>
{
	pArea.Docs.forEach((pDoc) =>
	{
		const tmpHash = hashFor(pArea.Area, pDoc.Topic);
		DOCS.push({ Hash: tmpHash, Area: pArea.Area, Title: pDoc.Title, Nav: pDoc.Nav, File: pDoc.File });
		(pDoc.Routes || []).forEach((pRoute) => ROUTE_DOC.push({ Route: pRoute, Hash: tmpHash }));
	});
});

/**
 * Resolve a route to the Hash of the help doc F1 should open, or '' when the route has no platform doc (so the
 * caller falls back to the file-based route help). Longest-matching route prefix wins.
 * @param {string} pRoute @returns {string}
 */
function hashForRoute(pRoute)
{
	const tmpRoute = String(pRoute || '');
	let tmpHash = '';
	let tmpBestLength = -1;
	ROUTE_DOC.forEach((pEntry) =>
	{
		if ((tmpRoute === pEntry.Route || tmpRoute.indexOf(pEntry.Route + '/') === 0) && (pEntry.Route.length > tmpBestLength))
		{
			tmpHash = pEntry.Hash;
			tmpBestLength = pEntry.Route.length;
		}
	});
	return tmpHash;
}

/** The doc registry entry for a Hash, or null. @param {string} pHash */
function docByHash(pHash)
{
	return DOCS.find((pDoc) => pDoc.Hash === pHash) || null;
}

/** The Area a Hash belongs to (''), for grouping the in-panel nav. @param {string} pHash */
function areaForHash(pHash)
{
	const tmpDoc = docByHash(pHash);
	return tmpDoc ? tmpDoc.Area : '';
}

/**
 * Parse the Area straight out of a `Doc-<Area>-<Topic>` hash — works for topics that aren't in the static
 * registry (customer-authored / newly-created ones), where docByHash/areaForHash would return ''. @param {string} pHash
 */
function areaFromHash(pHash)
{
	const tmpParts = String(pHash || '').split('-');
	return ((tmpParts.length >= 3) && (tmpParts[0] === 'Doc')) ? tmpParts[1] : '';
}

/** All docs in an Area, in registry order — the sibling topics shown in the in-panel nav. @param {string} pArea */
function docsInArea(pArea)
{
	return DOCS.filter((pDoc) => pDoc.Area === pArea);
}

module.exports = { AREAS, DOCS, ROUTE_DOC, hashFor, hashForRoute, docByHash, areaForHash, areaFromHash, docsInArea };
